import React, { useEffect } from "react";
import { ShimmerElementProps } from "../../../../shared/props";
import { warnDeprecation } from "../../../../shared/warnDeprecation";
import { useViewport } from "../../../../styles";
import { ShimmerCircle } from "../../../Shimmer/ShimmerCircle/ShimmerCircle";
import { ShimmerLine } from "../../../Shimmer/ShimmerLine/ShimmerLine";
import { SlotStack, SlotStackItem } from "../../../Stacks/SlotStack/SlotStack";
import { Stack } from "../../../Stacks/Stack/Stack";
import { ListItemCanvas } from "../shared/ListItemCanvas";

export type ListItemVariantWidth = "SixSix" | "EightFour" | "SixThreeThree" | "ThreeThreeSix" | "SixTwoFour" | "SixFourTwo" | "ThreeThreeThreeThree" | "SixTwoTwoTwo" | "ThreeSixThree" | "EightTwoTwo";
export interface ListItemShimmerProps extends ShimmerElementProps {
    /**
     * Name for the component of the first column.
     */
    readonly firstColumnComponentName?: "Text" | "Callout" | "Icon" | "Persona" | "ListItemMedia";

    /**
     * Variant of the list item to shimmer. Default is undefined, which represents the OneColumnListItem.
     */
    readonly variant?: ListItemVariantWidth;
}

// tslint:disable: no-magic-numbers
const variantColumnWidths: { [key in ListItemVariantWidth]: readonly number[] } = {
    "SixSix": [6, 6],
    "EightFour": [8, 4],
    "SixThreeThree": [6, 3, 3],
    "ThreeThreeSix": [3, 3, 6],
    "ThreeSixThree": [3, 6, 3],
    "SixTwoFour": [6, 2, 4],
    "SixFourTwo": [6, 4, 2],
    "ThreeThreeThreeThree": [3, 3, 3, 3],
    "SixTwoTwoTwo": [6, 2, 2, 2],
    "EightTwoTwo": [8, 2, 2]
};

/** @deprecated Please use the new ListItem/MobileListItem with ShimmerColumns instead. */
export const ListItemShimmer: React.FC<ListItemShimmerProps> = ({
    firstColumnComponentName = "Text",
    variant
}) => {
    useEffect(() => {
        warnDeprecation("ListItemShimmer has been deprecated, please use one of the new ListItem with ShimmerColumns instead.");
    }, []);

    const viewportType = useViewport();
    const viewportIsSmallerThanLarge = (viewportType === "s" || viewportType === "m");
    const firstItemSize = viewportIsSmallerThanLarge ? 16 : 32;

    let firstColumnShimmer = (
        <ShimmerLine height={viewportIsSmallerThanLarge ? 15 : 20} />
    );

    switch (firstColumnComponentName) {
        case "Icon":
            firstColumnShimmer = (
                <Stack width={firstItemSize} margin="0 10px 0 0">
                    <ShimmerCircle height={firstItemSize} />
                </Stack>
            );
            break;
        case "Persona":
            firstColumnShimmer = (
                <Stack horizontal={true} verticalAlign={"center"}>
                    <Stack width={firstItemSize} margin="0 10px 0 0">
                        <ShimmerCircle height={firstItemSize} />
                    </Stack>
                    <ShimmerLine height={firstItemSize} width={150} />
                </Stack>
            );
            break;
        case "ListItemMedia":
            firstColumnShimmer = (
                <Stack horizontal={true} childrenGap="0 10px" verticalAlign={"center"}>
                    <ShimmerLine height={firstItemSize} width={firstItemSize} />
                    <ShimmerLine height={firstItemSize} width={150} />
                </Stack>
            );
            break;
    }

    const firstColumnWidth = variant ? variantColumnWidths[variant][0] : 12;

    if (viewportIsSmallerThanLarge) {
        return (
            <ListItemCanvas>
                <Stack backgroundColor={"neutralHigh"} margin={"0 0 3px 0"} padding={"8px 13px 4px 52px"} verticalAlign={"center"} >
                    <Stack margin="0 0 4px 0">
                        {firstColumnShimmer}
                    </Stack>
                    {variant && variantColumnWidths[variant].slice(1).map((_, index) => (
                        <Stack margin="0 0 4px 0" key={index}>
                            <ShimmerLine height={20} />
                        </Stack>
                    ))}
                </Stack>
            </ListItemCanvas>
        );
    }

    return (
        <ListItemCanvas>
            <SlotStack backgroundColor={"neutralHigh"} totalSlots={12} height={48} margin={"0 0 3px 0"} padding={"0 13px 0 52px"} verticalAlign={"center"} >
                <SlotStackItem slots={firstColumnWidth}>
                    {firstColumnShimmer}
                </SlotStackItem>
                {variant && variantColumnWidths[variant].slice(1).map((width, index) => (
                    <SlotStackItem slots={width} key={index}>
                        <ShimmerLine height={20} />
                    </SlotStackItem>
                ))}
            </SlotStack>
        </ListItemCanvas>
    );
};
